import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Mail, Loader2 } from 'lucide-react';
import { checkAdminExists, setupAdmin, loginAdmin } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // If already authenticated, redirect to admin
    if (isAuthenticated) {
      navigate('/admin');
      return;
    }

    // Check if admin exists
    const checkSetup = async () => {
      try {
        const { admin_exists } = await checkAdminExists();
        setIsSetup(!admin_exists);
      } catch (error) {
        console.error('Failed to check admin status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSetup();
  }, [isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isSetup) {
        // Setup mode - create admin
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setSubmitting(false);
          return;
        }

        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setSubmitting(false);
          return;
        }

        await setupAdmin({
          username: formData.username,
          email: formData.email,
          password: formData.password
        });

        toast.success('Admin account created! Please log in.');
        setIsSetup(false);
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
      } else {
        // Login mode
        const response = await loginAdmin({
          username: formData.username,
          password: formData.password
        });

        login(response.user);
        toast.success(`Welcome back, ${response.user.username}!`);
        navigate('/admin');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <Loader2 className="animate-spin text-[var(--accent-blush-dark)]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="lg" />
          </div>
          <h1 className="heading-2 text-[var(--text-heading)]">
            {isSetup ? 'Create Admin Account' : 'Admin Login'}
          </h1>
          <p className="body-small text-[var(--text-secondary)] mt-2">
            {isSetup 
              ? 'Set up your admin credentials to get started' 
              : 'Enter your credentials to access the admin panel'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-[var(--border-light)]">
          <div className="space-y-5">
            {/* Username */}
            <div>
              <label className="block body-small font-medium mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter username"
                  className="w-full pl-11 pr-4 py-3 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                  data-testid="login-username-input"
                />
              </div>
            </div>

            {/* Email (setup only) */}
            {isSetup && (
              <div>
                <label className="block body-small font-medium mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter email"
                    className="w-full pl-11 pr-4 py-3 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block body-small font-medium mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter password"
                  className="w-full pl-11 pr-12 py-3 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm Password (setup only) */}
            {isSetup && (
              <div>
                <label className="block body-small font-medium mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    placeholder="Confirm password"
                    className="w-full pl-11 pr-4 py-3 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none transition-colors"
                    data-testid="login-confirm-password-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {isSetup ? 'Creating Account...' : 'Logging in...'}
              </>
            ) : (
              <>
                <Lock size={18} />
                {isSetup ? 'Create Account' : 'Login'}
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center body-small text-[var(--text-secondary)] mt-6">
          <a href="/" className="text-[var(--accent-blush-dark)] hover:underline">
            ← Back to Website
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
